// src/hooks/useReportDelivery.ts
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { apiFetch } from "@/config/api";
import {
  ReportRecipient,
  CreateReportRecipient,
  ReportListItem,
  CreateReportListItem,
  UpdateReportListItem,
  ReportScheduling,
  CreateReportScheduling,
  UpdateReportScheduling,
  ExecuteReportResult,
} from "@/types/reportDelivery";

// =============================================================================
// RECIPIENTS
// =============================================================================

export function useReportRecipients(filters?: { dest?: string; reportID?: number }) {
  const params = new URLSearchParams();
  if (filters?.dest) params.append("dest", filters.dest);
  if (filters?.reportID) params.append("reportID", filters.reportID.toString());
  const queryString = params.toString() ? `?${params.toString()}` : "";

  return useQuery<ReportRecipient[]>({
    queryKey: ["report-recipients", filters],
    queryFn: () => apiFetch(`/report-delivery/recipients${queryString}`),
  });
}

export function useCreateReportRecipient() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateReportRecipient | { dests: string[]; reportIDs: number[] }) =>
      apiFetch("/report-delivery/recipients", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({ queryKey: ["report-recipients"] });
      const created = result.created_count || 1;
      const skipped = result.skipped_count || 0;
      if (skipped > 0) {
        toast({
          title: "Destinataires ajoutés",
          description: `${created} association(s) créée(s), ${skipped} déjà existante(s).`,
        });
      } else {
        toast({
          title: "Destinataires ajoutés",
          description: `${created} association(s) créée(s) avec succès.`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useDeleteReportRecipient() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/report-delivery/recipients/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-recipients"] });
      toast({
        title: "Destinataire supprimé",
        description: "Le destinataire a été supprimé avec succès.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useRecipientEmails() {
  return useQuery<string[]>({
    queryKey: ["report-recipient-emails"],
    queryFn: () => apiFetch("/report-delivery/recipients/emails"),
  });
}

export function useSetReportRecipients() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ reportID, dests }: { reportID: number; dests: string[] }) =>
      apiFetch(`/report-delivery/reports/${reportID}/recipients`, {
        method: "PUT",
        body: JSON.stringify({ dests }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-recipients"] });
      queryClient.invalidateQueries({ queryKey: ["report-recipient-emails"] });
      toast({ title: "Destinataires mis à jour", description: "Les destinataires ont été enregistrés." });
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Erreur", description: error.message });
    },
  });
}

// =============================================================================
// REPORTS
// =============================================================================

export function useReportList(filters?: { active?: boolean }) {
  const params = new URLSearchParams();
  if (filters?.active !== undefined) params.append("active", filters.active.toString());
  const queryString = params.toString() ? `?${params.toString()}` : "";

  return useQuery<ReportListItem[]>({
    queryKey: ["report-list", filters],
    queryFn: () => apiFetch(`/report-delivery/reports${queryString}`),
  });
}

export function useReportListItem(reportID: number) {
  return useQuery<ReportListItem>({
    queryKey: ["report-list", reportID],
    queryFn: () => apiFetch(`/report-delivery/reports/${reportID}`),
    enabled: !!reportID,
  });
}

export function useCreateReportListItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateReportListItem) =>
      apiFetch("/report-delivery/reports", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-list"] });
      toast({
        title: "Rapport créé",
        description: "Le rapport a été créé avec succès.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useUpdateReportListItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ reportID, data }: { reportID: number; data: UpdateReportListItem }) =>
      apiFetch(`/report-delivery/reports/${reportID}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-list"] });
      toast({
        title: "Rapport mis à jour",
        description: "Le rapport a été mis à jour avec succès.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useDeleteReportListItem() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (reportID: number) =>
      apiFetch(`/report-delivery/reports/${reportID}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-list"] });
      queryClient.invalidateQueries({ queryKey: ["report-recipients"] });
      queryClient.invalidateQueries({ queryKey: ["report-scheduling"] });
      toast({
        title: "Rapport supprimé",
        description: "Le rapport et ses associations ont été supprimés.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useExecuteReport() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (reportID: number) =>
      apiFetch(`/report-delivery/execute/${reportID}`, { method: "POST" }),
    onSuccess: (data: ExecuteReportResult) => {
      const successCount = data.results.filter((r) => r.success).length;
      const errorCount = data.results.filter((r) => !r.success).length;

      toast({
        title: "Rapport exécuté",
        description: `${successCount} envoi(s) réussi(s), ${errorCount} erreur(s)`,
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur d'exécution",
        description: error.message,
      });
    },
  });
}

// =============================================================================
// SCHEDULING
// =============================================================================

export function useReportScheduling(filters?: { reportID?: number; send_hour?: number }) {
  const params = new URLSearchParams();
  if (filters?.reportID) params.append("reportID", filters.reportID.toString());
  if (filters?.send_hour !== undefined) params.append("send_hour", filters.send_hour.toString());
  const queryString = params.toString() ? `?${params.toString()}` : "";

  return useQuery<ReportScheduling[]>({
    queryKey: ["report-scheduling", filters],
    queryFn: () => apiFetch(`/report-delivery/scheduling${queryString}`),
  });
}

export function useCreateReportScheduling() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateReportScheduling) =>
      apiFetch("/report-delivery/scheduling", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-scheduling"] });
      toast({
        title: "Planning créé",
        description: "Le planning a été créé avec succès.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useUpdateReportScheduling() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateReportScheduling }) =>
      apiFetch(`/report-delivery/scheduling/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-scheduling"] });
      toast({
        title: "Planning mis à jour",
        description: "Le planning a été mis à jour avec succès.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

export function useDeleteReportScheduling() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/report-delivery/scheduling/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-scheduling"] });
      toast({
        title: "Planning supprimé",
        description: "Le planning a été supprimé avec succès.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message,
      });
    },
  });
}

